import { Search, X } from 'lucide-react';
import * as React from 'react';
import type { TaskListFilters } from '../../hooks/useTaskListFilters';
import {
  type GroupByOption,
  isTaskOverdue,
  type Task,
  type TaskStatusMeta,
  type TimelineFilter,
  type WorkspaceMember,
} from '../../lib/task-list';
import { cn } from '../../lib/utils';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

export interface TasksFilterBarProps {
  filters: TaskListFilters;
  statuses: TaskStatusMeta[];
  members: WorkspaceMember[];
  showGroupBy: boolean;
  tasks: Task[];
  currentUserId?: number;
}

export function TasksFilterBar({
  filters,
  statuses,
  members,
  showGroupBy,
  tasks,
  currentUserId,
}: TasksFilterBarProps) {
  const overdueCount = React.useMemo(
    () => tasks.filter((task) => isTaskOverdue(statuses, task)).length,
    [tasks, statuses]
  );

  const myTasksCount = React.useMemo(
    () => (currentUserId ? tasks.filter((t) => t.assignedTo === currentUserId).length : 0),
    [tasks, currentUserId]
  );

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Mutually-Exclusive Scope Segmented Control */}
      <div className="inline-flex p-1 rounded-lg bg-muted/60 border border-border/40 gap-1 w-fit">
        <button
          type="button"
          onClick={() => filters.setScope('all')}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-md transition-all select-none flex items-center gap-1.5',
            filters.scope === 'all'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <span>All Tasks</span>
          <span
            className={cn(
              'text-[10px] px-1.5 py-0.5 rounded-full',
              filters.scope === 'all'
                ? 'bg-muted text-foreground font-semibold'
                : 'bg-muted/50 text-muted-foreground'
            )}
          >
            {tasks.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => filters.setScope('me')}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-md transition-all select-none flex items-center gap-1.5',
            filters.scope === 'me'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <span>Assigned to Me</span>
          <span
            className={cn(
              'text-[10px] px-1.5 py-0.5 rounded-full',
              filters.scope === 'me'
                ? 'bg-muted text-foreground font-semibold'
                : 'bg-muted/50 text-muted-foreground'
            )}
          >
            {myTasksCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => filters.setScope('overdue')}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-md transition-all select-none flex items-center gap-1.5',
            filters.scope === 'overdue'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <span>Overdue</span>
          <span
            className={cn(
              'text-[10px] px-1.5 py-0.5 rounded-full',
              filters.scope === 'overdue'
                ? 'bg-destructive/15 text-destructive font-semibold'
                : overdueCount > 0
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-muted/50 text-muted-foreground'
            )}
          >
            {overdueCount}
          </span>
        </button>
      </div>

      {/* Main Filter Toolbar: Search + Select Dropdowns */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tasks..."
            className="pl-10 bg-card"
            value={filters.searchQuery}
            onChange={(e) => filters.setSearchQuery(e.target.value)}
          />
        </div>

        {showGroupBy && (
          <Select
            value={filters.groupBy}
            onValueChange={(val) => filters.setGroupBy(val as GroupByOption)}
          >
            <SelectTrigger className="w-full md:w-[170px] bg-card">
              <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Grouping</SelectItem>
              <SelectItem value="assignee">Group: Assignee</SelectItem>
              <SelectItem value="status">Group: Status</SelectItem>
              <SelectItem value="priority">Group: Priority</SelectItem>
              <SelectItem value="dueDate">Group: Due Date</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Select value={filters.activeTab} onValueChange={(val) => filters.setActiveTab(val)}>
          <SelectTrigger className="w-full md:w-[160px] bg-card">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status.key} value={status.key}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {!filters.isAssigneeFilterLocked && (
          <Select
            value={
              filters.selectedAssignees.length === 1 && filters.selectedAssignees[0] !== 'me'
                ? filters.selectedAssignees[0]
                : 'all'
            }
            onValueChange={(val) => {
              if (val === 'all') {
                filters.setSelectedAssignees([]);
              } else {
                filters.setSelectedAssignees([val]);
              }
            }}
          >
            <SelectTrigger className="w-full md:w-[170px] bg-card">
              <SelectValue placeholder="All Assignees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {members.map((member) => (
                <SelectItem key={member.id} value={String(member.id)}>
                  {member.fullName || member.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select
          value={
            filters.selectedPriorities.length === 1 ? String(filters.selectedPriorities[0]) : 'all'
          }
          onValueChange={(val) => {
            if (val === 'all') {
              filters.setSelectedPriorities([]);
            } else {
              filters.setSelectedPriorities([Number(val)]);
            }
          }}
        >
          <SelectTrigger className="w-full md:w-[150px] bg-card">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="2">High</SelectItem>
            <SelectItem value="1">Medium</SelectItem>
            <SelectItem value="0">Low</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.selectedTimeline === 'overdue' ? 'all' : filters.selectedTimeline}
          onValueChange={(val) => filters.setSelectedTimeline(val as TimelineFilter)}
        >
          <SelectTrigger className="w-full md:w-[150px] bg-card">
            <SelectValue placeholder="Due Date" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Due Dates</SelectItem>
            <SelectItem value="today">Due Today</SelectItem>
            <SelectItem value="week">Due This Week</SelectItem>
            <SelectItem value="none">No Due Date</SelectItem>
          </SelectContent>
        </Select>

        {filters.hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={filters.resetFilters}
            className="text-muted-foreground hover:text-foreground h-9 px-2 text-xs font-medium"
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
